/**
 * MANAGEMENT (15) — ANALYTICS i18n. FR default + AR-RTL, registered once from the module's onRegister()
 * (alongside registerManagementMessages) so the flagship Analytics section has its strings the moment the
 * console mounts. Keys are namespaced under `mgmt.analytics.*`. Layout mirrors automatically under
 * dir="rtl" via CSS logical properties; codes / amounts / dates stay LTR with .nv-ltr-island. Missing AR
 * key → the FR value → the raw key (never a crash, never a blank).
 *
 * REUSED KEYS — the panels also read a handful of already-registered Overview keys (never re-declared
 * here): the period labels `mgmt.overview.period.{30d,90d,12mo}`, the subscription-statut labels
 * `mgmt.overview.subStatut.*`, and the support-category labels `mgmt.overview.cat.*`.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── nav + header ────────────────────────────────────────────────────────────────
  "nav.mgmt.analytics": "Analytique",
  "mgmt.analytics.title": "Analytique de la plateforme",
  "mgmt.analytics.subtitle":
    "Le centre de reporting multi-laboratoires : revenus, abonnements, réseau et usage.",
  "mgmt.analytics.error": "Impossible de charger cette analyse.",
  "mgmt.analytics.tabs.aria": "Sections analytiques",

  // ── shared chart / table strings ──────────────────────────────────────────────────
  "mgmt.analytics.chart.empty.title": "Pas encore de données",
  "mgmt.analytics.chart.empty.desc":
    "Les données s'afficheront dès que l'activité de la plateforme aura commencé.",
  "mgmt.analytics.chart.donut.sr": "Répartition en {n} segments.",
  "mgmt.analytics.table.item": "Élément",
  "mgmt.analytics.table.share": "Part",

  // ── tabs ────────────────────────────────────────────────────────────────────────
  "mgmt.analytics.tab.revenue": "Revenus",
  "mgmt.analytics.tab.subscriptions": "Abonnements",
  "mgmt.analytics.tab.wilayas": "Wilayas",
  "mgmt.analytics.tab.referrals": "Réseau labo–médecin",
  "mgmt.analytics.tab.support": "Support par type",
  "mgmt.analytics.tab.modules": "Modules demandés",
  "mgmt.analytics.tab.analyzers": "Automates utilisés",
  "mgmt.analytics.tab.analysis-types": "Analyses demandées",

  // ── Revenue ───────────────────────────────────────────────────────────────────────
  "mgmt.analytics.revenue.mrr": "Revenu récurrent (MRR)",
  "mgmt.analytics.revenue.mrr.hint": "Run-rate mensuel des abonnements actifs.",
  "mgmt.analytics.revenue.arr": "Revenu annualisé (ARR)",
  "mgmt.analytics.revenue.arr.hint": "MRR × 12.",
  "mgmt.analytics.revenue.settled": "Encaissé",
  "mgmt.analytics.revenue.settled.hint": "{n} paiement(s) réglé(s) sur la période.",
  "mgmt.analytics.revenue.priced": "Abonnements tarifés",
  "mgmt.analytics.revenue.priced.hint": "{n} sans tarif catalogue.",
  "mgmt.analytics.revenue.subs": "abonnements",
  "mgmt.analytics.revenue.unpriced": "(plan retiré)",
  "mgmt.analytics.revenue.unpricedHint": "Plan absent du catalogue — n'entre pas dans le MRR.",
  "mgmt.analytics.revenue.trend.title": "Encaissements sur la période",
  "mgmt.analytics.revenue.trend.aria": "Courbe des encaissements réglés sur la période.",
  "mgmt.analytics.revenue.byTier.title": "MRR par palier",
  "mgmt.analytics.revenue.byTier.aria": "Répartition du revenu récurrent par palier de plan.",
  "mgmt.analytics.revenue.byPlan.title": "Revenu récurrent par plan",
  "mgmt.analytics.revenue.byPlan.metric": "MRR",

  // ── Subscriptions ───────────────────────────────────────────────────────────────
  "mgmt.analytics.subs.active": "Abonnements actifs",
  "mgmt.analytics.subs.totalKpi": "Abonnements totaux",
  "mgmt.analytics.subs.total": "total",
  "mgmt.analytics.subs.new": "Nouveaux",
  "mgmt.analytics.subs.noPlan": "(sans plan)",
  "mgmt.analytics.subs.planSub": "{total} au total",
  "mgmt.analytics.subs.donut.title": "Répartition par statut",
  "mgmt.analytics.subs.donut.aria": "Répartition des abonnements par statut.",
  "mgmt.analytics.subs.byPlan.title": "Abonnements par plan",

  // ── Wilayas ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.wilayas.total": "Laboratoires actifs",
  "mgmt.analytics.wilayas.distinct": "Wilayas couvertes",
  "mgmt.analytics.wilayas.bars.title": "Distribution géographique",
  "mgmt.analytics.wilayas.bars.aria": "Nombre de laboratoires actifs par wilaya.",
  "mgmt.analytics.wilayas.table.title": "Cohortes par wilaya",
  "mgmt.analytics.wilayas.metric": "Laboratoires",

  // ── Referrals (lab ↔ doctor) ──────────────────────────────────────────────────────
  "mgmt.analytics.ref.links": "Liens labo–médecin",
  "mgmt.analytics.ref.approved": "Liens approuvés",
  "mgmt.analytics.ref.approved.hint": "{i} invitation(s) en attente.",
  "mgmt.analytics.ref.pct": "Rétrocession moyenne",
  "mgmt.analytics.ref.pct.hint": "de {min} à {max} · sur {n} lien(s) rémunéré(s).",
  "mgmt.analytics.ref.pct.none": "Aucun lien rémunéré.",
  "mgmt.analytics.ref.newReturns": "Nouveaux retours",
  "mgmt.analytics.ref.free": "Gratuit",
  "mgmt.analytics.ref.paid": "Rémunéré",
  "mgmt.analytics.ref.freePaid.title": "Gratuit vs rémunéré",
  "mgmt.analytics.ref.freePaid.aria": "Liens approuvés gratuits contre rémunérés.",
  "mgmt.analytics.ref.band.none": "Aucune demande",
  "mgmt.analytics.ref.band.low": "Faible",
  "mgmt.analytics.ref.band.medium": "Moyenne",
  "mgmt.analytics.ref.band.high": "Élevée",
  "mgmt.analytics.ref.bands.title": "Niveaux de demande",
  "mgmt.analytics.ref.bands.aria": "Laboratoires répartis par niveau de demande de retours.",
  "mgmt.analytics.ref.topLabs.title": "Laboratoires les plus demandeurs",
  "mgmt.analytics.ref.lab": "Laboratoire n° {n}",
  "mgmt.analytics.ref.returns": "Retours",

  // ── Support by type ──────────────────────────────────────────────────────────────
  "mgmt.analytics.support.total": "Tickets totaux",
  "mgmt.analytics.support.openTotal": "Tickets ouverts",
  "mgmt.analytics.support.open": "ouverts",
  "mgmt.analytics.support.bars.title": "Tickets par catégorie",
  "mgmt.analytics.support.bars.aria": "Nombre de tickets par catégorie.",
  "mgmt.analytics.support.table.title": "Détail par catégorie",
  "mgmt.analytics.support.trend.title": "Tickets ouverts par catégorie",
  "mgmt.analytics.support.trend.aria": "Tendance des tickets ouverts par catégorie sur la période.",

  // ── Modules ───────────────────────────────────────────────────────────────────────
  "mgmt.analytics.modules.total": "Requêtes totales",
  "mgmt.analytics.modules.distinct": "Modules actifs",
  "mgmt.analytics.modules.bars.title": "Modules les plus demandés",
  "mgmt.analytics.modules.bars.aria": "Nombre de requêtes par module.",
  "mgmt.analytics.modules.routes.title": "Routes les plus demandées",
  "mgmt.analytics.modules.requests": "Requêtes",

  // ── Analyzers ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.analyzers.total": "Résultats produits",
  "mgmt.analytics.analyzers.count": "Automates actifs",
  "mgmt.analytics.analyzers.bars.title": "Automates les plus utilisés",
  "mgmt.analytics.analyzers.bars.aria": "Volume de résultats par automate.",
  "mgmt.analytics.analyzers.table.title": "Détail par automate",
  "mgmt.analytics.analyzers.results": "Résultats",
  "mgmt.analytics.analyzers.errors": "erreurs",

  // ── Analysis-types ────────────────────────────────────────────────────────────────
  "mgmt.analytics.types.total": "Demandes totales",
  "mgmt.analytics.types.distinct": "Paramètres distincts",
  "mgmt.analytics.types.bars.title": "Analyses les plus demandées",
  "mgmt.analytics.types.bars.aria": "Volume de demande par paramètre d'analyse.",
  "mgmt.analytics.types.table.title": "Détail par paramètre",
  "mgmt.analytics.types.demand": "Demande",
  "mgmt.analytics.types.source.ligne_demande": "Source : demandes",
  "mgmt.analytics.types.source.resultat": "Source : résultats",
};

const AR: Record<string, string> = {
  // ── nav + header ────────────────────────────────────────────────────────────────
  "nav.mgmt.analytics": "التحليلات",
  "mgmt.analytics.title": "تحليلات المنصة",
  "mgmt.analytics.subtitle": "مركز التقارير متعدد المخابر: الإيرادات والاشتراكات والشبكة والاستخدام.",
  "mgmt.analytics.error": "تعذّر تحميل هذا التحليل.",
  "mgmt.analytics.tabs.aria": "أقسام التحليلات",

  // ── shared ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.chart.empty.title": "لا توجد بيانات بعد",
  "mgmt.analytics.chart.empty.desc": "ستظهر البيانات بمجرد بدء نشاط المنصة.",
  "mgmt.analytics.chart.donut.sr": "توزيع إلى {n} أجزاء.",
  "mgmt.analytics.table.item": "العنصر",
  "mgmt.analytics.table.share": "الحصة",

  // ── tabs ────────────────────────────────────────────────────────────────────────
  "mgmt.analytics.tab.revenue": "الإيرادات",
  "mgmt.analytics.tab.subscriptions": "الاشتراكات",
  "mgmt.analytics.tab.wilayas": "الولايات",
  "mgmt.analytics.tab.referrals": "شبكة المخبر–الطبيب",
  "mgmt.analytics.tab.support": "الدعم حسب النوع",
  "mgmt.analytics.tab.modules": "الوحدات الأكثر طلبًا",
  "mgmt.analytics.tab.analyzers": "الأجهزة المستخدمة",
  "mgmt.analytics.tab.analysis-types": "التحاليل المطلوبة",

  // ── Revenue ───────────────────────────────────────────────────────────────────────
  "mgmt.analytics.revenue.mrr": "الإيراد المتكرر الشهري",
  "mgmt.analytics.revenue.mrr.hint": "المعدل الشهري للاشتراكات النشطة.",
  "mgmt.analytics.revenue.arr": "الإيراد السنوي",
  "mgmt.analytics.revenue.arr.hint": "الإيراد الشهري × 12.",
  "mgmt.analytics.revenue.settled": "المحصّل",
  "mgmt.analytics.revenue.settled.hint": "{n} دفعة مسددة خلال الفترة.",
  "mgmt.analytics.revenue.priced": "اشتراكات مسعّرة",
  "mgmt.analytics.revenue.priced.hint": "{n} دون سعر في الكتالوج.",
  "mgmt.analytics.revenue.subs": "اشتراكات",
  "mgmt.analytics.revenue.unpriced": "(خطة موقوفة)",
  "mgmt.analytics.revenue.unpricedHint": "خطة غير موجودة في الكتالوج — لا تدخل في الإيراد.",
  "mgmt.analytics.revenue.trend.title": "المحصّلات خلال الفترة",
  "mgmt.analytics.revenue.trend.aria": "منحنى المحصّلات المسددة خلال الفترة.",
  "mgmt.analytics.revenue.byTier.title": "الإيراد حسب المستوى",
  "mgmt.analytics.revenue.byTier.aria": "توزيع الإيراد المتكرر حسب مستوى الخطة.",
  "mgmt.analytics.revenue.byPlan.title": "الإيراد المتكرر حسب الخطة",
  "mgmt.analytics.revenue.byPlan.metric": "الإيراد الشهري",

  // ── Subscriptions ───────────────────────────────────────────────────────────────
  "mgmt.analytics.subs.active": "اشتراكات نشطة",
  "mgmt.analytics.subs.totalKpi": "إجمالي الاشتراكات",
  "mgmt.analytics.subs.total": "الإجمالي",
  "mgmt.analytics.subs.new": "جديدة",
  "mgmt.analytics.subs.noPlan": "(بدون خطة)",
  "mgmt.analytics.subs.planSub": "{total} إجمالًا",
  "mgmt.analytics.subs.donut.title": "التوزيع حسب الحالة",
  "mgmt.analytics.subs.donut.aria": "توزيع الاشتراكات حسب الحالة.",
  "mgmt.analytics.subs.byPlan.title": "الاشتراكات حسب الخطة",

  // ── Wilayas ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.wilayas.total": "مخابر نشطة",
  "mgmt.analytics.wilayas.distinct": "الولايات المغطّاة",
  "mgmt.analytics.wilayas.bars.title": "التوزيع الجغرافي",
  "mgmt.analytics.wilayas.bars.aria": "عدد المخابر النشطة حسب الولاية.",
  "mgmt.analytics.wilayas.table.title": "الفئات حسب الولاية",
  "mgmt.analytics.wilayas.metric": "المخابر",

  // ── Referrals ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.ref.links": "روابط المخبر–الطبيب",
  "mgmt.analytics.ref.approved": "الروابط المعتمدة",
  "mgmt.analytics.ref.approved.hint": "{i} دعوة قيد الانتظار.",
  "mgmt.analytics.ref.pct": "متوسط العمولة",
  "mgmt.analytics.ref.pct.hint": "من {min} إلى {max} · على {n} رابط مدفوع.",
  "mgmt.analytics.ref.pct.none": "لا توجد روابط مدفوعة.",
  "mgmt.analytics.ref.newReturns": "إحالات جديدة",
  "mgmt.analytics.ref.free": "مجاني",
  "mgmt.analytics.ref.paid": "مدفوع",
  "mgmt.analytics.ref.freePaid.title": "مجاني مقابل مدفوع",
  "mgmt.analytics.ref.freePaid.aria": "الروابط المعتمدة المجانية مقابل المدفوعة.",
  "mgmt.analytics.ref.band.none": "بلا طلب",
  "mgmt.analytics.ref.band.low": "منخفض",
  "mgmt.analytics.ref.band.medium": "متوسط",
  "mgmt.analytics.ref.band.high": "مرتفع",
  "mgmt.analytics.ref.bands.title": "مستويات الطلب",
  "mgmt.analytics.ref.bands.aria": "توزيع المخابر حسب مستوى طلب الإحالات.",
  "mgmt.analytics.ref.topLabs.title": "المخابر الأكثر طلبًا",
  "mgmt.analytics.ref.lab": "المخبر رقم {n}",
  "mgmt.analytics.ref.returns": "الإحالات",

  // ── Support ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.support.total": "إجمالي التذاكر",
  "mgmt.analytics.support.openTotal": "التذاكر المفتوحة",
  "mgmt.analytics.support.open": "مفتوحة",
  "mgmt.analytics.support.bars.title": "التذاكر حسب الفئة",
  "mgmt.analytics.support.bars.aria": "عدد التذاكر حسب الفئة.",
  "mgmt.analytics.support.table.title": "التفصيل حسب الفئة",
  "mgmt.analytics.support.trend.title": "التذاكر المفتوحة حسب الفئة",
  "mgmt.analytics.support.trend.aria": "اتجاه التذاكر المفتوحة حسب الفئة خلال الفترة.",

  // ── Modules ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.modules.total": "إجمالي الطلبات",
  "mgmt.analytics.modules.distinct": "الوحدات النشطة",
  "mgmt.analytics.modules.bars.title": "الوحدات الأكثر طلبًا",
  "mgmt.analytics.modules.bars.aria": "عدد الطلبات لكل وحدة.",
  "mgmt.analytics.modules.routes.title": "المسارات الأكثر طلبًا",
  "mgmt.analytics.modules.requests": "الطلبات",

  // ── Analyzers ─────────────────────────────────────────────────────────────────────
  "mgmt.analytics.analyzers.total": "النتائج المنتَجة",
  "mgmt.analytics.analyzers.count": "الأجهزة النشطة",
  "mgmt.analytics.analyzers.bars.title": "الأجهزة الأكثر استخدامًا",
  "mgmt.analytics.analyzers.bars.aria": "حجم النتائج لكل جهاز.",
  "mgmt.analytics.analyzers.table.title": "التفصيل حسب الجهاز",
  "mgmt.analytics.analyzers.results": "النتائج",
  "mgmt.analytics.analyzers.errors": "أخطاء",

  // ── Analysis-types ────────────────────────────────────────────────────────────────
  "mgmt.analytics.types.total": "إجمالي الطلبات",
  "mgmt.analytics.types.distinct": "معايير مختلفة",
  "mgmt.analytics.types.bars.title": "التحاليل الأكثر طلبًا",
  "mgmt.analytics.types.bars.aria": "حجم الطلب لكل معيار تحليل.",
  "mgmt.analytics.types.table.title": "التفصيل حسب المعيار",
  "mgmt.analytics.types.demand": "الطلب",
  "mgmt.analytics.types.source.ligne_demande": "المصدر: الطلبات",
  "mgmt.analytics.types.source.resultat": "المصدر: النتائج",
};

/** Register the analytics section strings — call once from the module's onRegister(). */
export function registerAnalyticsMessages(): void {
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
